// --- React & Redux
import React, { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
// --- External UI Libraries
import { Col, Row } from 'antd';
import Button from '@dtci/spellbook/Button';
import Typography from '@dtci/spellbook/Typography';
// --- Components
import { PaginationBar } from 'datastage/components/PaginationBar';
import ToastNotificationProvider from 'datastage/components/ToastNotifications';
import { PMXDeliveriesGrid } from './components/grid';
import { PMXDeliveriesSearchForm } from './components/search-form';
import { TwoRowsLayout } from './layout/two-row';
// --- Hooks & Utils
import { useToasts } from 'datastage/utils/hooks/useToasts';
import { getItemRange, getAllItemsRange } from 'datastage/utils/pagination';
import { exportToCsv } from 'datastage/utils/export/exportToCsv';
import { parseSearchParams, updateSearchParams } from 'datastage/utils/urlParams';
import { useStoredItemsPerPageSync } from 'datastage/utils/hooks/useStoredItemsPerPageSync';
import { PAGINATION_STORAGE_KEYS } from 'datastage/utils/paginationPreferences';
// --- API & Store
import { usePmxDeliveriesData } from './hooks/usePmxDeliveriesData';

/**
 * PMX Deliveries Page
 *
 * Features:
 * - Displays a paginated list of PMX deliveries
 * - Provides search, export, and refresh functionality
 * - Handles loading and toast notifications
 * - Uses a two-row layout for search and grid
 * - Implements refreshPmxDeliveries flag pattern for clean separation of concerns
 * - Persists page size preference to localStorage automatically
 *
 * @component
 * @returns {JSX.Element} The rendered PMX deliveries management page.
 */
export const PMXDeliveriesPage = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const { showErrorToast } = useToasts();
    const { fetchPmxDeliveries, updatePMXDeliveries, pmxDeliveriesRange, itemsPerPage } =
        usePmxDeliveriesData();
    const { totalPages, currentPage, totalItems, refreshPmxDeliveries, pageSizeOptions } = useSelector(
        (state) => state.pmxDeliveries,
    );

    /**
     * Sync persisted page size preference from localStorage on mount.
     * If stored preference differs from Redux state, automatically updates Redux.
     */
    useStoredItemsPerPageSync({
        storageKey: PAGINATION_STORAGE_KEYS.pmxDeliveries,
        itemsPerPage,
        rangeKey: 'pmxDeliveriesRange',
        onSync: updatePMXDeliveries,
    });

    /**
     * Listen for refreshPmxDeliveries flag and refresh the grid.
     * This pattern allows other components to trigger refreshes
     * without duplicating fetch logic.
     */
    useEffect(() => {
        if (refreshPmxDeliveries) {
            handleRefresh();
            updatePMXDeliveries({ refreshPmxDeliveries: false });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [refreshPmxDeliveries]);

    /**
     * Handle page change for pagination (←/→ buttons).
     * Fetches directly without updating URL (TAP-UI pattern).
     * Page size changes go through URL → search form handles those.
     */
    const handlePageChange = async (pageNumber) => {
        if (pageNumber === currentPage) return;

        const nextPageRange = getItemRange(pageNumber, itemsPerPage);
        const data = await fetchPmxDeliveries(nextPageRange);

        if (data) {
            updatePMXDeliveries({
                deliveries: data?.content ?? [],
                totalPages: data?.totalPages ?? 0,
                totalItems: data?.totalElements ?? 0,
                currentPage: data?.pageable?.pageNumber ?? 0,
                pmxDeliveriesRange: nextPageRange,
            });
        }
    };

    /**
     * Handle page size change from dropdown.
     * Updates URL with new page size - the search form will handle the fetch.
     * This prevents duplicate fetches (TAP-UI pattern).
     */
    const handlePageSizeChange = (newSize) => {
        if (newSize === itemsPerPage) return;

        // Update URL with new page size
        // Search form will see URL change and fetch with new size
        const currentParams = parseSearchParams(location.search);
        updateSearchParams(location, navigate, {
            ...currentParams,
            pageSize: newSize,
        });
    };

    /**
     * Refresh the PMX deliveries list from the API.
     */
    const handleRefresh = async () => {
        const data = await fetchPmxDeliveries(pmxDeliveriesRange);

        if (data) {
            updatePMXDeliveries({
                deliveries: data?.content ?? [],
                totalItems: data?.totalElements ?? 0,
                totalPages: data?.totalPages ?? 0,
                currentPage: data?.pageable?.pageNumber ?? 0,
            });
        }
    };

    /**
     * Export all PMX Deliveries to CSV.
     * Fetches all records using the total count from the current result set.
     */
    const handleExport = async () => {
        const exportRange = getAllItemsRange(totalItems);
        const data = await fetchPmxDeliveries(exportRange);

        if (data?.code) {
            showErrorToast(data.message || 'An unexpected error occurred');
            return;
        }

        exportToCsv(data?.content || [], 'delivery_pmx');
    };

    return (
        <>
            <Row
                style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginTop: 20,
                }}
            >
                <Col>
                    <Typography variant="T50" style={{ marginLeft: 20 }}>
                        PMX Deliveries
                    </Typography>
                </Col>
                <Col style={{ display: 'flex', gap: 8 }}>
                    <Button
                        iconType="fa-solid fa-arrow-down-to-line"
                        labelText="Export"
                        styleType="link"
                        onClick={handleExport}
                        disabled={totalItems === 0}
                    >
                        Export
                    </Button>
                    <Button
                        iconType="fa-solid fa-rotate"
                        labelText="Refresh"
                        styleType="link"
                        onClick={handleRefresh}
                    >
                        Refresh
                    </Button>
                </Col>
            </Row>

            <TwoRowsLayout>
                <main data-position="top">
                    <PMXDeliveriesSearchForm />
                </main>
                <main data-position="bottom">
                    <PMXDeliveriesGrid />
                </main>
            </TwoRowsLayout>

            <PaginationBar
                totalPages={totalPages}
                currentPage={currentPage}
                totalItems={totalItems}
                itemsPerPage={itemsPerPage}
                onPageChange={handlePageChange}
                onPageSizeChange={handlePageSizeChange}
                pageSizeOptions={pageSizeOptions}
            />

            <ToastNotificationProvider />
        </>
    );
};
